import * as React from 'react';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import FormField from '@/components/Questionnaire/form/FormField';
import Buttons from '@/components/Buttons';
import ArrowCircleUp from '@/components/Icons/ArrowCircleUp';
import ArrowCircleDown from '@/components/Icons/ArrowCircleDown';
import { Questionnaire } from '@/typings/Questionnaire';
import { useOpenDataReferencePane } from '@/panes/hooks';
import Eye from '@components/Icons/Eye';
import Spacer from '@components/Spacer';

import TopLineButton from './TopLineButton';
import s from './FormPreview.module.scss';
import { useFormEditorContext } from '../FormEditor/FormEditorContext';

type Props = {
  item: Questionnaire.Field;
  subFields?: Questionnaire.Field[];
  isSelected: boolean;
  assignees: string[];
  currentSelectedField?: string;
  onClick: (args: { field: Questionnaire.Field; isInList: boolean }) => void;
  conditionExpression?: Questionnaire.Expression;
  fields: Questionnaire.Field[];
  onReorder?: (args: {
    item: Pick<Questionnaire.Field, 'id' | 'rank' | 'listFieldTemplateId'>;
    direction: 'up' | 'down';
  }) => void;
  reorderDownDisabled?: boolean;
  reorderUpDisabled?: boolean;
};

const FormItem: React.FC<Props> = ({
  assignees,
  conditionExpression,
  currentSelectedField,
  isSelected,
  item,
  subFields,
  onClick,
  fields,
  onReorder,
  reorderUpDisabled,
  reorderDownDisabled,
}) => {
  const { control } = useForm();
  const { setSelectedFieldEditorTab } = useFormEditorContext();
  const setDataReferenceId = useOpenDataReferencePane();

  const [areSubfieldsVisible, setAreSubfieldsVisible] = React.useState(true);

  const hasConditions = conditionExpression?.expression
    || (conditionExpression?.variables && conditionExpression.variables.length > 0);

  const hasAssignees = assignees && assignees.length > 0;

  const hasPdfFill = false; // @todo — replace with actual data

  const handleReorderButton = (direction: 'up' | 'down'): void => {
    if (onReorder) {
      onReorder({ direction, item });
    }
  };

  return (
    <div>
      {(hasAssignees || hasConditions || hasPdfFill) && (
        <div className={s.topLine}>
          {hasConditions && (
            <TopLineButton
              type="conditions"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onClick({ field: item, isInList: false });
                setSelectedFieldEditorTab('conditions');
              }}
            />
          )}
          {hasAssignees && (
            <TopLineButton
              type="assignees"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onClick({ field: item, isInList: false });
                setSelectedFieldEditorTab('assignees');
              }}
            />
          )}
          {hasPdfFill && (
            <TopLineButton
              type="pdf-fill"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onClick({ field: item, isInList: false });
                setSelectedFieldEditorTab('pdf-fill');
              }}
            />
          )}
        </div>
      )}
      {isSelected && (
        <div className={s.reorderHandle}>
          <Buttons
            isWrapper
            onClick={() => handleReorderButton('up')}
            disabled={reorderUpDisabled}
          >
            <ArrowCircleUp weight="fill" color="var(--color-noodle)" size={20} />
          </Buttons>
          <span className={s.reorderHandleNumber}>{item.rank}</span>
          <Buttons
            isWrapper
            onClick={() => handleReorderButton('down')}
            disabled={reorderDownDisabled}
          >
            <ArrowCircleDown weight="fill" color="var(--color-noodle)" size={20} />
          </Buttons>
        </div>
      )}
      <FormField
        field={item}
        control={control}
        rules={{}}
        isEditor
        onClickPopulatedFrom={(e: React.MouseEvent) => {
          e.stopPropagation();
          onClick({ field: item, isInList: false });
          setSelectedFieldEditorTab('autofill');
          if (item.autofill?.expression?.variables?.[0]?.referenceType === 'data-reference-slug') {
            setDataReferenceId({
              dataReferenceId: item.autofill?.expression?.variables?.[0]?.referenceId,
            });
          }
        }}
      />
      {item.type === 'list' && (
        <>
          <Buttons isWrapper onClick={() => setAreSubfieldsVisible(prev => !prev)}>
            <Eye />
          </Buttons>
          <Spacer size={8} />
        </>
      )}
      {subFields && subFields.length > 0 && areSubfieldsVisible && (
        <div className={s.listItemsContainer}>
          {subFields.map((subField) => {
            const fullSubField = fields.find((f) => f.key === subField.key);
            if (!fullSubField) {
              return null;
            }
            return (
              <div
                key={subField.id}
                id={subField.id}
                className={classNames(s.field, subField.id === currentSelectedField && s.selectedField)}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onClick({
                    field: {
                      ...fullSubField,
                      listFieldTemplateId: subField.listFieldTemplateId,
                    },
                    isInList: item.type === 'list',
                  });
                  setSelectedFieldEditorTab('settings');
                }}
              >
                <FormItem
                  key={subField.id}
                  item={fullSubField}
                  assignees={[]}
                  fields={fields}
                  subFields={fullSubField.listItemTemplates}
                  currentSelectedField={currentSelectedField}
                  isSelected={subField.id === currentSelectedField}
                  conditionExpression={subField.conditionExpression || undefined}
                  onClick={onClick}
                  onReorder={onReorder}
                  reorderUpDisabled={subFields[0].id === subField.id}
                  reorderDownDisabled={subFields[subFields.length - 1].id === subField.id}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FormItem;
